import { resolveParameterDefault } from './parameterDefaults';
import { omitDisallowedParameterValues } from './parameterOptions';
import { isReservedParameterName } from './reservedParameters';
const resolveDefinitionDefaults = (definitions, now, timezone) => Object.entries(definitions).reduce((acc, [key, definition]) => {
    const value = resolveParameterDefault(definition, now, timezone ?? undefined);
    return value === undefined ? acc : { ...acc, [key]: value };
}, {});
export const getEffectiveParameterDefinitions = ({ projectDefinitions, exploreDefinitions, }) => ({
    ...projectDefinitions,
    ...exploreDefinitions,
});
export const resolveFallbackParameterValues = ({ projectDefinitions, exploreDefinitions, virtualViewSavedValues, now, timezone, }) => ({
    ...resolveDefinitionDefaults(projectDefinitions, now, timezone),
    ...resolveDefinitionDefaults(exploreDefinitions, now, timezone),
    ...omitDisallowedParameterValues(virtualViewSavedValues, getEffectiveParameterDefinitions({
        projectDefinitions,
        exploreDefinitions,
    })),
});
// A chart's saved value applies only when the dashboard has no value and the definition no default
export const canUseChartSavedParameterValue = ({ key, dashboardValues, definitions, }) => dashboardValues[key] === undefined &&
    definitions[key]?.default === undefined;
// Targeted: dashboard value, else chart-saved only when the definition has no default.
// Untargeted: chart-saved values only.
export const getDashboardTileParameterOverrides = ({ definitions, dashboardValues: rawDashboardValues, chartSavedValues: rawChartSavedValues, isTargeted, }) => {
    // Values outside a parameter's fixed options count as unset
    const dashboardValues = omitDisallowedParameterValues(rawDashboardValues, definitions);
    const chartSavedValues = omitDisallowedParameterValues(rawChartSavedValues, definitions);
    if (!isTargeted)
        return chartSavedValues;
    const keys = new Set([
        ...Object.keys(chartSavedValues),
        ...Object.keys(dashboardValues),
    ]);
    return [...keys].reduce((acc, key) => {
        const dashboardValue = dashboardValues[key];
        if (dashboardValue !== undefined) {
            return { ...acc, [key]: dashboardValue };
        }
        const chartSavedValue = chartSavedValues[key];
        if (chartSavedValue === undefined ||
            !canUseChartSavedParameterValue({
                key,
                dashboardValues,
                definitions,
            })) {
            return acc;
        }
        return { ...acc, [key]: chartSavedValue };
    }, {});
};
export const resolveDashboardTileParameters = ({ fallbackSources, ...inputs }) => ({
    ...resolveFallbackParameterValues(fallbackSources),
    ...getDashboardTileParameterOverrides({
        ...inputs,
        definitions: getEffectiveParameterDefinitions(fallbackSources),
    }),
});
export var DashboardTileParameterSource;
(function (DashboardTileParameterSource) {
    DashboardTileParameterSource["DASHBOARD"] = "dashboard";
    DashboardTileParameterSource["CHART"] = "chart";
    DashboardTileParameterSource["DEFAULT"] = "default";
})(DashboardTileParameterSource || (DashboardTileParameterSource = {}));
// Where a tile's value for one parameter comes from; default covers the whole fallback chain.
export const getDashboardTileParameterSource = ({ key, ...inputs }) => {
    if (getDashboardTileParameterOverrides(inputs)[key] === undefined) {
        return DashboardTileParameterSource.DEFAULT;
    }
    const dashboardValues = omitDisallowedParameterValues(inputs.dashboardValues, inputs.definitions);
    return inputs.isTargeted && dashboardValues[key] !== undefined
        ? DashboardTileParameterSource.DASHBOARD
        : DashboardTileParameterSource.CHART;
};
// Referenced keys with no dashboard value and no definition default, paired with
// whether each referencing tile has a chart-saved value for them.
const getKeysResolvedPerTile = ({ tiles, dashboardValues: rawDashboardValues, definitions, }) => {
    const dashboardValues = omitDisallowedParameterValues(rawDashboardValues, definitions);
    return tiles.reduce((acc, tile) => {
        const chartSavedValues = omitDisallowedParameterValues(tile.chartSavedValues, definitions);
        new Set(tile.parameterReferences).forEach((key) => {
            if (isReservedParameterName(key) ||
                !canUseChartSavedParameterValue({
                    key,
                    dashboardValues,
                    definitions,
                })) {
                return;
            }
            acc.set(key, [
                ...(acc.get(key) ?? []),
                chartSavedValues[key] !== undefined,
            ]);
        });
        return acc;
    }, new Map());
};
// A key is missing when any referencing tile has no dashboard value, default or chart-saved value.
export const getMissingRequiredDashboardParameters = (inputs) => [...getKeysResolvedPerTile(inputs)]
    .filter(([, hasChartValues]) => hasChartValues.some((has) => !has))
    .map(([key]) => key);
//# sourceMappingURL=dashboardTileParameters.js.map